import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpContext,
  HttpContextToken,
  HttpHeaders,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  News,
  NewsImage,
  NewsResponse,
} from '../../features/news/models/news.model';
import { AuthService } from '../../core/services/auth.service';

/**
 * Token de contexto HTTP para indicar que una petición
 * debe saltar la autenticación
 */
export const SKIP_AUTH_TOKEN = new HttpContextToken<boolean>(() => false);

/**
 * 📰 **Landing News Service**
 *
 * Servicio de SOLO LECTURA para cargar noticias en la landing page.
 * 🚧 MODO PRUEBA: Temporalmente usa autenticación para testing
 */
@Injectable({
  providedIn: 'root',
})
export class LandingNewsService {
  private readonly apiUrl = environment.apiUrl;
  private authService = inject(AuthService);

  constructor(private http: HttpClient) {}

  /**
   * Obtener noticias con paginación
   * @param page - Número de página (0-based)
   * @param size - Tamaño de página
   */
  getNews(
    page: number = 0,
    size: number = 10,
    sort: string = 'createdAt,desc',
    categoryId?: number,
  ): Observable<NewsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (sort) {
      params = params.set('sort', sort);
    }

    if (categoryId) {
      return this.http.get<NewsResponse>(
        `${this.apiUrl}/api/news/category/${categoryId}`,
        {
          params,
        },
      );
    }

    return this.http.get<NewsResponse>(`${this.apiUrl}/api/news`, {
      params,
    });
  }

  /**
   * Obtener imágenes de una noticia específica
   * @param newsId - ID de la noticia
   */
  getNewsImages(newsId: number): Observable<NewsImage[]> {
    return this.http.get<NewsImage[]>(
      `${this.apiUrl}/api/news-images/by-news/${newsId}`,
    );
  }

  /**
   * Obtener una noticia por ID
   * @param newsId - ID de la noticia
   */
  getNewsById(newsId: number): Observable<News> {
    return this.http.get<News>(`${this.apiUrl}/api/news/${newsId}`);
  }

  /**
   * Obtener videos del canal (públicos) con paginación
   * @param page - Número de página (0-based)
   * @param size - Tamaño de página
   */
  getChannelVideos(page: number = 0, size: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<any>(`${this.apiUrl}/api/channel-videos/public`, {
      params,
    });
  }
}
